import { useState } from 'react';
import MainModal from '../MainModal';
import useAuth from '@/hooks/useAuth';
import { useUserCouncil } from '@/hooks/useUserCouncil';
import { useRecoilState } from 'recoil';
import { showInitiateRetakeState } from '@/atoms';
import { IconInfoCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { Button, Group, Stack, Text, Alert, Select } from '@mantine/core';
import { useGetUserExamStatus, useInitiateRetake } from '@/services/exam-retakes';

export default function InitiateRetakeModal() {
    const { user } = useAuth();
  const council = useUserCouncil();
    const { mutate: initiateRetake, isPending: initiatingRetake } = useInitiateRetake();
    const { data: examStatus, isLoading: loadingStatus } = useGetUserExamStatus(user?.id);
    const [show, setShow] = useRecoilState(showInitiateRetakeState);
    const [selectedProgramId, setSelectedProgramId] = useState<string>('');

    const handleClose = () => {
        setShow(false);
    }

    const handleInitiateRetake = () => {
        if (!selectedProgramId) {
            notifications.show({
                title: 'Error',
                message: 'Please select an education program.',
                color: 'red'
            });
            return;
        }

        if (!council?.id) {
            notifications.show({
                title: 'Error',
                message: 'Council information not found.',
                color: 'red'
            });
            return;
        }

        initiateRetake({
            councilId: council?.id ?? "",
            educationProgramId: selectedProgramId,
        }, {
            onSuccess: () => {
                notifications.show({
                    title: 'Success',
                    message: 'Retake initiated successfully. You can now schedule your retake exam.',
                    color: 'green'
                });
                handleClose();
            }
        });
    };

    // Get eligible programs for retake
    const eligiblePrograms = examStatus?.data?.educationPrograms?.filter(program => program.canRetake) || [];
    const selectedProgram = eligiblePrograms.find(program => program.educationProgramId === selectedProgramId);

    return (
        <MainModal
            open={show}
            onClose={handleClose}
            title="Initiate Exam Retake"
            size="md"
        >
            <Stack gap="md">
                {loadingStatus ? (
                    <Text size="sm" c="dimmed">Loading exam status...</Text>
                ) : (
                    <>
                        <div className="p-3 bg-blue-50 rounded-md">
                            <Text size="sm" fw={500}>Council: {council?.councilName}</Text>
                            <Text size="sm" c="dimmed">
                                Total Programs: {examStatus?.data?.totalPrograms || 0} | 
                                Eligible for Retake: {examStatus?.data?.eligiblePrograms || 0}
                            </Text>
                        </div>

                        {eligiblePrograms.length > 0 ? (
                            <>
                                <Select
                                    label="Select Education Program"
                                    placeholder="Choose a program for retake"
                                    value={selectedProgramId}
                                    onChange={(value) => setSelectedProgramId(value || '')}
                                    data={eligiblePrograms.map(program => ({
                                        value: program.educationProgramId,
                                        label: `${program.educationProgramName} (${program.councilName})`
                                    }))}
                                    required
                                />

                                {selectedProgram && (
                                    <div className="p-3 bg-green-50 rounded-md">
                                        <Text size="sm" fw={500}>Selected Program: {selectedProgram.educationProgramName}</Text>
                                        <Text size="sm" c="dimmed">
                                            Remaining attempts: {selectedProgram.remainingAttempts} / {selectedProgram.maxAllowedAttempts}
                                        </Text>
                                        <Text size="sm" c="dimmed">
                                            Total attempts: {selectedProgram.totalAttempts}
                                        </Text>
                                        {selectedProgram.nextAllowedDate && (
                                            <Text size="sm" c="dimmed">
                                                Next allowed date: {new Date(selectedProgram.nextAllowedDate).toLocaleDateString()}
                                            </Text>
                                        )}
                                    </div>
                                )}
                            </>
                        ) : (
                            <Alert
                                icon={<IconInfoCircle size={16} />}
                                title="No Eligible Programs"
                                color="yellow"
                            >
                                <Text size="sm">
                                    You don't have any education programs eligible for retake at this time.
                                </Text>
                            </Alert>
                        )}

                        <Alert
                            icon={<IconInfoCircle size={16} />}
                            title="Retake Information"
                            color="blue"
                        >
                            <div className="space-y-2">
                                <Text size="sm">
                                    • Retake attempts are automatically approved - no payment proof required
                                </Text>
                                <Text size="sm">
                                    • You can have a maximum of 3 attempts per education program
                                </Text>
                                <Text size="sm">
                                    • Once initiated, you can schedule your retake exam immediately
                                </Text>
                                <Text size="sm">
                                    • Users with passing results (≥60%) cannot request retakes
                                </Text>
                            </div>
                        </Alert>

                        <Group justify="flex-end" gap="md">
                            <Button variant="outline" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleInitiateRetake}
                                loading={initiatingRetake}
                                disabled={!selectedProgramId || !selectedProgram?.canRetake}
                            >
                                Initiate Retake
                            </Button>
                        </Group>
                    </>
                )}
            </Stack>
        </MainModal>
    );
}
